/**
 * Tijdzone-helpers voor de CLIENT — de tegenhanger van server/src/tijd.js.
 *
 * ⚠️ NOOIT new Date().toISOString().slice(0, 10) voor een kalenderdag. toISOString()
 * rekent naar UTC, en tussen 00:00 en 02:00 Nederlandse zomertijd (00:00-01:00 in de
 * winter) levert dat de VORIGE dag. Dit project heeft die fout vier keer gemaakt:
 * server-side gefixt in a5f46fd, client-side in de back-upnaam, de verlof-defaults en
 * `vastgelegdOp`.
 *
 * Een kale toISOString() als TIJDSTIP blijft wél goed — dat is een instant, geen
 * kalenderdag. Alleen het afleiden van een dag is fout.
 *
 * Twee functies, want er zijn twee verschillende vragen:
 *
 *   vandaagNL()  → "welke kalenderdag is het NU in Nederland?"
 *                  Forceert Europe/Amsterdam, net als de server. Zo schrijven client
 *                  en server dezelfde dag weg, ook als de browser in een andere
 *                  tijdzone staat (reizende gebruiker, verkeerd ingestelde laptop) —
 *                  en `vastgelegdOp` blijft vergelijkbaar met wat de server noteert.
 *
 *   isoDag(d)    → "wat is de kalenderdag VAN DEZE Date, in zijn eigen lokale tijd?"
 *                  Voor Date-objecten die zelf al lokaal zijn opgebouwd (parseDag,
 *                  of een cursor die per dag opschuift). Hier zou Europe/Amsterdam
 *                  forceren juist verkeerd zijn: een lokaal middernacht-object zou
 *                  in een verre tijdzone naar de vorige dag verschuiven.
 */
const TIJDZONE = 'Europe/Amsterdam';

// en-CA levert 'YYYY-MM-DD', dus er is niets handmatig samen te stellen.
// Intl met een expliciete tijdzone in plaats van een vaste offset: zo klopt het
// ook rond de zomer-/wintertijdovergang, zonder externe library.
const DATUM_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIJDZONE, year: 'numeric', month: '2-digit', day: '2-digit',
});

/** De kalenderdatum van NU in Nederland als 'JJJJ-MM-DD'. */
export function vandaagNL(nu = new Date()) {
  return DATUM_FORMAT.format(nu);
}

/**
 * Date → 'JJJJ-MM-DD' op de LOKALE componenten van dat object.
 * Niet via toISOString: die rekent in UTC en schuift een dag.
 */
export function isoDag(datum) {
  const maand = String(datum.getMonth() + 1).padStart(2, '0');
  const dag = String(datum.getDate()).padStart(2, '0');
  return `${datum.getFullYear()}-${maand}-${dag}`;
}

/** 'JJJJ-MM-DD' → Date op LOKALE middernacht. 'T00:00:00' houdt het uit UTC. */
export function parseDag(iso) {
  return new Date(`${iso}T00:00:00`);
}

/** 'JJJJ-MM-DD' plus n dagen, weer als 'JJJJ-MM-DD'. Rolt over maand/jaar heen. */
export function dagPlus(iso, dagen) {
  const d = parseDag(iso);
  d.setDate(d.getDate() + dagen);
  return isoDag(d);
}

// Klokdeel van de bestandsstempel. hourCycle 'h23' i.p.v. hour12: false, want dat
// laatste levert op sommige ICU-versies "24-00-00" om middernacht i.p.v. "00-00-00".
const KLOK_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIJDZONE, hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

/**
 * Stempel voor EXPORT-BESTANDSNAMEN: 'JJJJ-MM-DD_uu-mm-ss' in Nederlandse tijd.
 *
 * Vervangt new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19), dat op TWEE
 * manieren misging: om 00:05 Nederlandse zomertijd noemde het het bestand naar GISTEREN
 * (22:05 UTC), en het klokdeel stond het hele jaar door een of twee uur mis. Gemeten aan
 * de echte export van 22 aug 2026 om 10:35 NL: die heette sass-rooster-log-2026-08-22T08-35-12.
 *
 * De tijd blijft er bewust in: zonder klokdeel krijgen twee exports op dezelfde dag
 * dezelfde naam en maakt de browser er "(1)" van.
 *
 * Underscore tussen dag en klok in plaats van de T van ISO: dit is een bestandsnaam,
 * geen ISO-tijdstip meer, en een T zou suggereren dat er UTC in staat.
 */
export function bestandsstempelNL(nu = new Date()) {
  return `${vandaagNL(nu)}_${KLOK_FORMAT.format(nu).replace(/:/g, '-')}`;
}
